import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { OfflinePin } from '@material-ui/icons';
import { OfflineValuesContext } from 'contexts';
import {
  ChapterListItem,
  DynamicHeading,
  IconTooltip,
  NavigateToLocation,
} from 'components';
import {
  getDepth,
  getMarkerTitle,
  getPageLabel,
} from 'components/ChapterListItem/ChapterListItemHelpers';

const useStyles = makeStyles((theme) => ({
  chapterLink: {
    color: theme.palette.greyBlue['700'],
    cursor: 'pointer',
  },
  endBlock: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end',
  },
  offlinePinWrapper: {
    color: theme.palette.greyBlue[600],
    display: 'flex',
    padding: theme.spacing(0.5),
  },
  offlinePinIcon: {
    fontSize: theme.spacing(2.5),
  },
  pageLabel: {
    ...theme.typography.body2,
    alignSelf: 'center',
    color: ({ isOffline }) =>
      isOffline ? theme.palette.text.disabled : theme.palette.greyBlue[700],
    minWidth: theme.spacing(3.75),
  },
  placeholder: {
    marginRight: theme.spacing(6.5),
  },
  title: {
    ...theme.typography.body1,
    alignSelf: 'center',
    color: ({ isOffline }) =>
      isOffline ? theme.palette.text.disabled : theme.palette.greyBlue[700],
    fontSize: ({ depth }) => {
      switch (depth) {
        case 1:
          return theme.typography.pxToRem(16);
        case 2:
          return theme.typography.pxToRem(14);
        case 3:
          return theme.typography.pxToRem(12);
        default:
          return theme.typography.pxToRem(12);
      }
    },
    paddingLeft: ({ isChild, depth }) =>
      isChild ? theme.spacing(1 * depth) : '',
  },
  titleContainer: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  titleWrapper: {
    alignItems: 'center',
    display: 'flex',
    paddingLeft: theme.spacing(2),
    width: '100%',

    '& .Mui-focusVisible': {
      outlineOffset: theme.spacing(0.25),
    },
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: 1,
  },
}));

const ToCListItem = ({
  hDepth, // dynamic heading
  isOffline,
  marker,
  onNavigateClick,
  ...props
}) => {
  const { checkIsChapterDownloaded } = useContext(OfflineValuesContext);
  // this condition only used to limit and set the default dynamic heading value
  if (!hDepth || hDepth < 3) {
    hDepth = 3;
  }
  const { bookLocation, children, index, isTopLevelMarker } = marker;
  const hasChildren = Boolean(children?.length);
  const isAvailableForOffline = checkIsChapterDownloaded(marker);

  const title = getMarkerTitle(marker);
  const pageLabel = getPageLabel(marker) ?? '';

  const showAsText = isOffline && !isAvailableForOffline;
  const depth = getDepth(marker);

  const classes = useStyles({
    depth,
    hasNoChildren: !hasChildren,
    isChild: !isTopLevelMarker,
    isOffline: showAsText,
  });

  const offlinePinForTOC = (
    <div className={classes.endBlock}>
      <div className={classes.offlinePinWrapper}>
        <IconTooltip
          id="toc-offline"
          placement="top"
          title={'Available for Offline'}
        >
          <OfflinePin
            aria-describedby="toc-offline"
            className={classes.offlinePinIcon}
          />
        </IconTooltip>
      </div>
      {!hasChildren && <div className={classes.placeholder}></div>}
    </div>
  );

  const styledTitle = (
    <DynamicHeading hDepth={hDepth}>
      <div className={`${classes.titleContainer} noTranslate`}>
        {pageLabel ? (
          <span className={classes.visuallyHidden}>Page&nbsp;</span>
        ) : null}
        <span className={classes.pageLabel}>{pageLabel}&nbsp;</span>
        <span className={classes.title}>{title}</span>
      </div>
    </DynamicHeading>
  );
  const chapterTitle = (
    <div className={classes.titleWrapper}>
      {showAsText ? (
        styledTitle
      ) : (
        <NavigateToLocation
          bookLocation={bookLocation}
          className={classes.chapterLink}
          onClick={() => onNavigateClick(bookLocation)}
          id={index}
        >
          {styledTitle}
        </NavigateToLocation>
      )}

      {isAvailableForOffline ? offlinePinForTOC : null}
    </div>
  );

  return (
    <ChapterListItem
      id={`ToC-${index}`}
      title={chapterTitle}
      ariaLabelChapterTitle={title}
      {...props}
    >
      {hasChildren &&
        children.map((m) => (
          <React.Fragment key={`ToC-${m.index}`}>
            <ToCListItem
              hDepth={hDepth + 1}
              isOffline={isOffline}
              marker={m}
              onNavigateClick={onNavigateClick}
            />
          </React.Fragment>
        ))}
    </ChapterListItem>
  );
};

ToCListItem.propTypes = {
  /**
   * Indicates whether this ToC item been downloaded for offline.
   */
  isOffline: PropTypes.bool.isRequired,
  /**
   * This is the component's data contents, should have { bookLocation, children, index, isTopLevelMarker }
   */
  marker: PropTypes.object.isRequired,
  onNavigateClick: PropTypes.func,
};

export default ToCListItem;
